import type { PhoneNumberListResponse, PhoneNumberItem, PhoneNumberAvailableItem } from './voiceAgentTypes';

/** Provider display labels */
export const PROVIDER_LABEL: Record<string, string> = {
  plivo: 'Plivo',
  twilio: 'Twilio',
};

/** Country ISO by provider for available/purchase APIs */
export const COUNTRY_BY_PROVIDER: Record<string, string> = {
  plivo: 'IN',
  twilio: 'US',
};

/** Phone provider options for voice agent forms (outbound/inbound) */
export const PHONE_PROVIDER_OPTIONS = [
  { value: 'plivo_IN', provider: 'plivo', country_iso: 'IN', label: '+91 (IN • Plivo)' },
  { value: 'twilio_US', provider: 'twilio', country_iso: 'US', label: '+1 (US • Twilio)' },
] as const;

export function getProviderLabel(provider?: string): string {
  if (!provider) return '—';
  const key = provider.toLowerCase();
  return PROVIDER_LABEL[key] ?? provider;
}

/** Get display string from list response default_number (single value). Empty string if none. */
export function getDefaultNumberFromResponse(res: PhoneNumberListResponse | undefined): string {
  if (!res?.default_number) return '';
  const d = res.default_number;
  if (Array.isArray(d)) {
    const first = d[0];
    return first?.number ?? first?.phone_number ?? '';
  }
  const item = d as { number?: string; phone_number?: string };
  return item?.number ?? item?.phone_number ?? '';
}

/** Normalize default_number from API to array of items. */
export function getDefaultNumberList(
  defaultNumber: string | PhoneNumberItem | PhoneNumberItem[] | undefined
): PhoneNumberItem[] {
  if (defaultNumber == null) return [];
  if (typeof defaultNumber === 'string')
    return [{ number: defaultNumber, phone_number: defaultNumber }];
  if (Array.isArray(defaultNumber)) return defaultNumber.filter(Boolean);
  return [defaultNumber];
}

export function getNumberDisplay(item: PhoneNumberItem | PhoneNumberAvailableItem | null | undefined): string {
  if (!item) return '—';
  return (item as PhoneNumberItem).number ?? (item as PhoneNumberItem).phone_number ?? (item as PhoneNumberAvailableItem).number ?? '—';
}

/** Format E.164 for display (e.g. +91 80353 15999). */
export function formatPhoneDisplay(num: string): string {
  const digits = num.replace(/\D/g, '');
  if (digits.startsWith('91') && digits.length === 12) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  if (digits.length >= 10) {
    return num.startsWith('+') ? num : `+${num}`;
  }
  return num;
}

export function isNumberInDefaultList(
  item: PhoneNumberItem,
  defaultList: PhoneNumberItem[]
): boolean {
  const num = getNumberDisplay(item);
  const normalized = num.replace(/\D/g, '');
  return defaultList.some((d) => {
    const dn = getNumberDisplay(d).replace(/\D/g, '');
    return dn && normalized && dn === normalized;
  });
}

/**
 * Parse from_number / provider list strings into provider key + E.164 `phoneNumber` for the form.
 * API values are typically strings with country code (e.g. +1…, +91…).
 */
export function parseFromNumber(fromNumber: string | null | undefined): { phoneCode: string; phoneNumber: string } {
  if (!fromNumber || String(fromNumber).trim() === '') {
    return { phoneCode: '', phoneNumber: '' };
  }
  const s = String(fromNumber).trim();
  const toE164 = (digits: string): string => {
    const d = digits.replace(/\D/g, '');
    return d ? `+${d}` : '';
  };

  if (s.startsWith('plivo_IN')) {
    const e164 = toE164(s.slice('plivo_IN'.length));
    return { phoneCode: 'plivo_IN', phoneNumber: e164 };
  }
  if (s.startsWith('twilio_US')) {
    const e164 = toE164(s.slice('twilio_US'.length));
    return { phoneCode: 'twilio_US', phoneNumber: e164 };
  }

  if (s.startsWith('+')) {
    const digits = s.slice(1).replace(/\D/g, '') || '';
    if (!digits) return { phoneCode: '', phoneNumber: '' };
    const e164 = `+${digits}`;
    if (digits.startsWith('1') && digits.length >= 10) return { phoneCode: 'twilio_US', phoneNumber: e164 };
    if (digits.startsWith('91') && digits.length >= 11) return { phoneCode: 'plivo_IN', phoneNumber: e164 };
    return { phoneCode: '', phoneNumber: e164 };
  }

  const digits = s.replace(/\D/g, '');
  if (digits) {
    const e164 = `+${digits}`;
    if (digits.startsWith('1') && digits.length >= 10) return { phoneCode: 'twilio_US', phoneNumber: e164 };
    if (digits.startsWith('91') && digits.length >= 11) return { phoneCode: 'plivo_IN', phoneNumber: e164 };
    return { phoneCode: '', phoneNumber: e164 };
  }

  return { phoneCode: '', phoneNumber: s };
}

/**
 * E.164 for API (no provider prefix). Accepts digits-only or an existing +… string from the provider list.
 * `phoneCode` is unused but kept for call-site clarity.
 */
export function formatPhoneForApi(_phoneCode: string, digits: string): string | null {
  const raw = String(digits ?? '').trim();
  if (!raw) return null;
  if (raw.startsWith('+')) {
    const d = raw.slice(1).replace(/\D/g, '');
    return d ? `+${d}` : null;
  }
  const d = raw.replace(/\D/g, '');
  return d ? `+${d}` : null;
}

/** Normalize stored/API from_number to E.164 (no twilio_US / plivo_IN prefix). */
export function normalizeFromNumberForApi(fromNumber: string | null | undefined): string | null {
  if (!fromNumber || String(fromNumber).trim() === '') return null;
  const s = String(fromNumber).trim();
  if (s.startsWith('+')) return formatPhoneForApi('', s);
  const { phoneNumber } = parseFromNumber(s);
  return formatPhoneForApi('', phoneNumber);
}

/** Dropdown options from phone list API: value is normalized E.164, label is the API string. */
export function getPhoneNumberOptionsFromList(res: PhoneNumberListResponse | undefined): Array<{ value: string; label: string }> {
  if (!res) return [];
  const defaultItems = getDefaultNumberList(res.default_number);
  const numberItems = res.numbers ?? [];
  const seen = new Set<string>();
  const options: Array<{ value: string; label: string }> = [];
  for (const item of [...defaultItems, ...numberItems]) {
    const raw =
      (item as { number?: string; phone_number?: string }).number ??
      (item as { number?: string; phone_number?: string }).phone_number ??
      '';
    const display = String(raw).trim();
    if (!display) continue;
    const { phoneNumber: value } = parseFromNumber(display);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: display });
  }
  return options;
}

/** Format location from available number item (city, region, country). */
export function formatAvailableNumberLocation(item: PhoneNumberAvailableItem): string {
  const parts = [item.city, item.region, item.country].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  return item.friendly_name ?? '—';
}
